import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { FaYinYang } from 'react-icons/fa';
import { mindfulnessService } from '../../../services/mindfulness.service';
import { useAuth } from '../../../context/AuthContext';
import './MindfulnessButton.css';
import '../components/WellnessButtons.css';

const MindfulnessButton = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [sessionLength, setSessionLength] = useState(5);
  const [isSessionStarted, setIsSessionStarted] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [breathingState, setBreathingState] = useState('');
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState(null);

  useEffect(() => {
    if (breathingState === 'inhale') {
      const timer = setTimeout(() => {
        setBreathingState('hold');
      }, 4000); // 4 seconds inhale
      return () => clearTimeout(timer);
    }
    if (breathingState === 'hold') {
      const timer = setTimeout(() => {
        setBreathingState('exhale');
      }, 4000); // 4 seconds hold
      return () => clearTimeout(timer);
    }
    if (breathingState === 'exhale') {
      const timer = setTimeout(() => {
        setBreathingState('inhale');
      }, 4000); // 4 seconds exhale
      return () => clearTimeout(timer);
    }
  }, [breathingState]);

  useEffect(() => {
    if (isSessionStarted && countdown === 0 && sessionTimeLeft > 0) {
      const timer = setInterval(() => {
        setSessionTimeLeft(prev => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isSessionStarted, countdown, sessionTimeLeft]);

  const handleStart = () => {
    setIsOpen(true);
  };

  const handleStartSession = () => {
    setIsSessionStarted(true);
    setSessionTimeLeft(sessionLength * 60);
    setSessionStartTime(Date.now());
    // Start countdown
    let count = 3;
    const countdownInterval = setInterval(() => {
      count--;
      setCountdown(count);
      if (count === 0) {
        clearInterval(countdownInterval);
        startBreathingCycle();
      }
    }, 1000);
  };

  const startBreathingCycle = () => {
    setBreathingState('inhale');
  };

  const handleEndSession = async () => {
    try {
      if (!user) {
        console.error('No user found');
        return;
      }

      // Calculate session details
      const sessionDuration = sessionLength * 60;
      const completedDuration = sessionDuration - sessionTimeLeft;
      const sessionText = `Completed ${Math.floor(completedDuration/60)} minutes and ${completedDuration%60} seconds of meditation`;

      // First update UI to show we're ending
      setIsSessionStarted(false);
      setBreathingState('');
      setIsOpen(false);
      setSessionLength(5);
      setCountdown(3);
      setSessionStartTime(null);

      // Then save to Firebase
      await mindfulnessService.createSession(user.uid, {
        text: sessionText
      });

    } catch (error) {
      console.error('Error saving mindfulness session:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div className="wellness-button" onClick={handleStart}>
        <div className="wellness-button-header">
          <div className="wellness-button-title">
            <FaYinYang />
            <h3>Mindfulness</h3>
          </div>
          <button className="wellness-button-expand">+</button>
        </div>
      </div>

      {isOpen && createPortal(
        <div className="mindfulness-overlay" onClick={(e) => {
          if (e.target === e.currentTarget && !isSessionStarted) setIsOpen(false);
        }}>
          <motion.div 
            className="mindfulness-modal"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="mindfulness-content">
              {!isSessionStarted ? (
                <>
                  <h2>Mindfulness Session</h2>
                  <p>Choose your session length</p>
                  <div className="session-length-selector">
                    {[5, 10, 15, 20].map((minutes) => (
                      <button
                        key={minutes}
                        className={`length-option ${sessionLength === minutes ? 'selected' : ''}`}
                        onClick={() => setSessionLength(minutes)}
                      >
                        {minutes} min
                      </button>
                    ))}
                  </div>
                  <button 
                    className="start-session-button"
                    onClick={handleStartSession}
                  >
                    Start Session
                  </button>
                </>
              ) : (
                <>
                  <h2>Breathing Guide</h2>
                  {countdown > 0 ? (
                    <div className="countdown">
                      <motion.div
                        key={countdown}
                        initial={{ scale: 2, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                      >
                        {countdown}
                      </motion.div>
                    </div>
                  ) : (
                    <div className="breathing-session">
                      <div className="session-time">
                        {formatTime(sessionTimeLeft)}
                      </div>
                      <div className="breathing-circle-container">
                        <motion.div 
                          className="breathing-circle"
                          animate={{
                            scale: breathingState === 'inhale' ? 1.5 : 
                                  breathingState === 'hold' ? 1.5 : 1,
                          }}
                          transition={{
                            duration: breathingState === 'inhale' ? 4 :
                                     breathingState === 'hold' ? 4 : 4,
                            ease: "easeInOut"
                          }}
                        >
                          <motion.span>
                            {breathingState === 'inhale' ? 'Inhale' :
                             breathingState === 'hold' ? 'Hold' :
                             breathingState === 'exhale' ? 'Exhale' : 'Breathe'}
                          </motion.span>
                        </motion.div>
                      </div>
                      <button 
                        className="end-session-button"
                        onClick={handleEndSession}
                      >
                        End Session
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </>
  );
};

export default MindfulnessButton; 